import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Alert,
  CircularProgress,
  FormControl,
  FormLabel,
} from '@mui/material';
import { styled, Theme } from '@mui/material/styles';
import { biometricService } from '../../services/biometricService';
import useMediaQuery from '@mui/material/useMediaQuery';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import UploadFile from '@mui/icons-material/UploadFile';
import CameraCapture from '../../components/CameraCapture';

const ImagePreview = styled('img')(({ theme }) => ({
  maxWidth: '100%',
  maxHeight: '300px',
  objectFit: 'contain',
  marginTop: '16px',
  borderRadius: '4px',
  border: '1px solid #ccc',
  [theme.breakpoints.down('sm')]: {
    maxHeight: '200px',
  },
}));

interface ValidationResult {
  isMatch: boolean;
  score: number;
  userId?: number;
  threshold: number;
}

const BiometricValidation = () => {
  const [validationType, setValidationType] = useState('1:1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ValidationResult | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [userId, setUserId] = useState<string>('');
  const [showCamera, setShowCamera] = useState(false);

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setError(null);
    setResult(null);

    // Criar apenas o preview
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
  };

  const handleCameraCapture = (file: File, previewUrl: string) => {
    setSelectedFile(file);
    setImagePreview(previewUrl);
    setShowCamera(false);
  };

  const handleValidation = async () => {
    if (!selectedFile) return;

    try {
      setLoading(true);
      setError(null);

      // Converter imagem para base64
      const base64Image = await convertFileToBase64(selectedFile);

      if (validationType === '1:N') {
        // Chamar API de busca biométrica
        const response = await biometricService.searchFace(base64Image);
        
        setResult({
          isMatch: response.is_match,
          score: response.score,
          userId: response.user_id,
          threshold: response.threshold,
        });
      } else {
        // Validação 1:1
        const response = await biometricService.verifyFace(userId, base64Image);
        
        setResult({
          isMatch: response.is_match,
          score: response.score,
          userId: parseInt(userId),
          threshold: response.threshold,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during validation');
    } finally {
      setLoading(false);
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result as string;
        // Remove the data:image/[type];base64, prefix
        resolve(base64String.split(',')[1]);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Validação Biométrica
      </Typography>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl>
            <FormLabel>Tipo de Validação</FormLabel>
            <RadioGroup
              value={validationType}
              onChange={(e) => setValidationType(e.target.value as '1:1' | '1:N')}
            >
              <FormControlLabel 
                value="1:1" 
                control={<Radio />} 
                label="Verificar com Usuário (1:1)" 
              />
              <FormControlLabel 
                value="1:N" 
                control={<Radio />} 
                label="Buscar Usuário (1:N)" 
              />
            </RadioGroup>
          </FormControl>

          <Box sx={{ mt: 3 }}>
            {!showCamera ? (
              <Box sx={{ display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
                <Button
                  variant="contained"
                  component="label"
                  disabled={loading}
                  fullWidth={isMobile}
                  startIcon={<UploadFile />}
                >
                  Upload Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>

                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setShowCamera(true)}
                  disabled={loading}
                  fullWidth={isMobile}
                  startIcon={<PhotoCamera />}
                >
                  Usar Câmera
                </Button>
              </Box>
            ) : (
              <CameraCapture
                onCapture={handleCameraCapture}
                onClose={() => setShowCamera(false)}
              />
            )}

            {imagePreview && !showCamera && (
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <ImagePreview src={imagePreview} alt="Preview" />
                
                {validationType === '1:1' && (
                  <TextField
                    sx={{ mt: 3, width: '100%', maxWidth: '400px' }}
                    label="User ID for comparison"
                    variant="outlined"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                  />
                )}

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleValidation}
                  disabled={loading || (validationType === '1:1' && !userId)}
                  sx={{ mt: 3 }}
                  fullWidth={isMobile}
                >
                  Validate Image
                </Button>
              </Box>
            )}
          </Box>

          {loading && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error}
            </Alert>
          )}

          {result && (
            <Box sx={{ mt: 3 }}>
              <Alert severity={result.isMatch ? "success" : "warning"}>
                {result.isMatch ? (
                  <>
                    Match found! User ID: {result.userId}<br />
                    Confidence Score: {(result.score * 100).toFixed(2)}%<br />
                    Threshold: {(result.threshold * 100).toFixed(2)}%
                  </>
                ) : (
                  "No match found in the database"
                )}
              </Alert>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default BiometricValidation; 